import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosService } from "../services/AxiosService";

/**
 * Hook to handle authentication failures and provide queue status
 */
export function useAxiosAuth() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthFailure = () => {
      console.log("Authentication failed, redirecting to sign-in...");

      // Clear all auth-related storage
      try {
        // Clear auth_user and project_link_params
        localStorage.removeItem("auth_user");
        localStorage.removeItem("project_link_params");

        // Clear any other auth-related localStorage items
        const localKeysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            (key.startsWith("auth_") ||
              key.includes("token") ||
              key.includes("user"))
          ) {
            localKeysToRemove.push(key);
          }
        }
        localKeysToRemove.forEach((key) => localStorage.removeItem(key));

        // Clear any auth-related sessionStorage items
        const sessionKeysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith("auth_") ||
              key.includes("token") ||
              key.includes("user"))
          ) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

        console.log("Cleared all auth-related storage");
      } catch (error) {
        console.error("Failed to clear storage:", error);
      }

      // Navigate to sign-in page
      navigate("/sign-in", { replace: true });
    };

    // Listen for auth failure events only
    // Note: auth:logout events are handled directly by the App component
    window.addEventListener("auth:failed", handleAuthFailure);

    return () => {
      window.removeEventListener("auth:failed", handleAuthFailure);
    };
  }, [navigate]);

  // Return queue status and utility functions
  return {
    getQueueStatus: () => axiosService.getQueueStatus(),
    clearQueue: () => axiosService.clearQueue(),
  };
}

/**
 * Hook to get current failed requests queue status (for debugging/monitoring)
 */
export function useAxiosQueueStatus() {
  // You could make this reactive with a state update mechanism if needed
  const getStatus = () => axiosService.getQueueStatus();

  return {
    getQueueStatus: getStatus,
    // Helper method to log current status
    logQueueStatus: () => {
      const status = getStatus();
      console.log("Axios Queue Status:", status);
      return status;
    },
  };
}
